import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth tokens live in an httpOnly, SameSite=Lax cookie set by the server — JavaScript
// cannot read them, so an XSS payload cannot exfiltrate the session. We keep
// withCredentials so the cookie flows on same-origin API calls.
export const api = axios.create({ baseURL: API, withCredentials: true });

// Calls that are expected to fail quietly: anonymous session probe (401 on every
// public page load), payment-status polling, and the auth forms (they show inline errors).
const SILENT_PATHS = [/\/auth\/me$/, /\/payments\/status\//, /\/auth\/login$/, /\/auth\/mfa/, /\/auth\/register$/, /\/track\//];

// Never fail silently: any API error surfaces a visible toast. Components with their
// own catch+toast may also fire — the stable id collapses repeats of the same failing call.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const cfg = err.config || {};
    const url = cfg.url || "";
    if (!cfg.silentError && !SILENT_PATHS.some((re) => re.test(url))) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : err.response ? `Request failed (${err.response.status}) — please try again`
        : "Network error — check your connection and try again";
      toast.error(msg, { id: `${cfg.method}:${url}:${err.response?.status || "net"}` });
    }
    return Promise.reject(err);
  }
);

// Backward-compatible no-ops: older call sites pass the token through, but the
// cookie is the source of truth and nothing is stored in localStorage anymore.
export const setToken = () => {};
export const getToken = () => null;

// 7-day signed links for the anonymous quick-check flow. These are NOT session
// credentials: a check token grants read/unlock access to one anonymous check for
// 7 days and is designed to travel in shareable links (same model as a
// password-reset URL). Auth session tokens remain in httpOnly cookies (above).
export const getCheckToken = (claimId) => localStorage.getItem(`dd_check_${claimId}`);
export const setCheckToken = (claimId, token) => localStorage.setItem(`dd_check_${claimId}`, token);
export const checkAuth = (claimId) => {
  const t = getCheckToken(claimId);
  return t ? { headers: { "X-Check-Token": t } } : {};
};
